import closeDownloadDocumentToast from "@iewccouk/Store/Handlers/Custom/CloseDownloadDocumentToast";
import { CustomState } from "@iewccouk/Store/Reducers";
import { colorStrings } from "@iewccouk/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: CustomReusableDownloadToasterStyles;
}

const mapStateToProps = (state: CustomState) => ({
    isLoading: state.customDownloads.isLoading,
    isSuccess: state.customDownloads.isSuccess,
});

const mapDispatchToProps = {
    closeDownloadDocumentToast,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomReusableDownloadToasterStyles {
    maskWrapper?: InjectableCss;
    maskToastOverlay?: InjectableCss;
    maskToast?: InjectableCss;
    loading?: LoadingSpinnerProps;
    message?: TypographyPresentationProps;
    errorCloseButton?: ButtonPresentationProps;
    errorCloseIcon?: IconPresentationProps;
}

export const customReusableDownloadToasterStyles: CustomReusableDownloadToasterStyles = {
    maskWrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: flex-end;
            margin-bottom: 50px;
            margin-right: 30px;
        `,
    },
    maskToastOverlay: {
        css: css`
            background-color: ${safeColor(getColor(colorStrings.commonGlobalWhite)).rgb().alpha(0.05).string()};
            height: 100%;
            left: 0;
            position: fixed;
            top: 0;
            width: 100%;
            z-index: 9998;
        `,
    },
    maskToast: {
        css: css`
            align-items: center;
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonBorder)};
            border-left: 3px solid ${getColor(colorStrings.infoMain)};
            box-shadow: 0 2px 4px 0 ${safeColor(getColor(colorStrings.commonGlobalBlack)).rgb().alpha(0.17).string()};
            display: inline-flex;
            gap: 10px;
            justify-content: space-between;
            min-height: 45px;
            padding: 5px;
            position: relative;
            width: 300px;
            z-index: 9999;
        `,
    },
    loading: {
        color: colorStrings.infoMain,
        size: 20,
        css: css``,
    },
    message: {
        variant: "legend",
        forwardAs: "span",
        css: css`
            width: 100%;
        `,
    },
    errorCloseButton: {
        variant: "primary",
        buttonType: "outline",
        css: css`
            align-self: flex-start;
            align-items: center;
            display: inline-flex;
            border: none;
            border-radius: 50%;
            height: 20px;
            justify-content: center;
            min-height: 20px;
            padding: 5px;
            width: 20px;
        `,
    },
    errorCloseIcon: {
        src: X,
        size: 10,
        css: css`
            &&& {
                top: 0;
            }
        `,
    },
};

const CustomReusableDownloadToaster = ({ isLoading, isSuccess, extendedStyles, closeDownloadDocumentToast }: Props) => {
    const [styles] = useState(() => mergeToNew(customReusableDownloadToasterStyles, extendedStyles));

    const handleOverlayClick = () => {
        if (!isLoading && !isSuccess) {
            closeDownloadDocumentToast({});
        }
    };

    if (!isLoading && isSuccess) {
        return null;
    }

    const borderStyle = css`
        border-left-color: ${getColor(colorStrings.dangerMain)};
    `;
    const maskToastStyle: InjectableCss = {
        css: css`
            ${styles.maskToast?.css}
            ${!isLoading && !isSuccess ? borderStyle : ""}
        `,
    };

    return (
        <StyledWrapper {...styles.maskWrapper}>
            <StyledWrapper {...styles.maskToastOverlay} onClick={handleOverlayClick}></StyledWrapper>
            <StyledWrapper {...maskToastStyle}>
                {isLoading && <LoadingSpinner {...styles.loading} />}
                {isLoading && (
                    <Typography {...styles.message}>{siteMessage("Pdf_Document_Preparing_Message")}</Typography>
                )}
                {!isLoading && !isSuccess && (
                    <>
                        <Typography {...styles.message} color={colorStrings.dangerMain}>
                            {siteMessage("Pdf_Document_Error_Message")}
                        </Typography>
                        <Button {...styles.errorCloseButton} onClick={() => closeDownloadDocumentToast({})}>
                            <ButtonIcon {...styles.errorCloseIcon} />
                            <VisuallyHidden id="close-download-indicator">{translate("Close modal")}</VisuallyHidden>
                        </Button>
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomReusableDownloadToaster as any) as any;
